import {
  getListPropControllerElementChildren,
  getListPropControllerFileIds,
  getListPropControllerPageIds,
  getListPropControllerSwatchIds,
  getListPropControllerTypographyIds,
  getShapePropControllerElementChildren,
  getShapePropControllerFileIds,
  getShapePropControllerPageIds,
  getShapePropControllerSwatchIds,
  getShapePropControllerTypographyIds,
  Types
} from "./descriptors";
import {
  ColorControlType,
  getListElementChildren,
  getListFileIds,
  getListPageIds,
  getListSwatchIds,
  getListTypographyIds,
  getShapeElementChildren,
  getShapeFileIds,
  getShapePageIds,
  getShapeSwatchIds,
  getShapeTypographyIds,
  getStyleSwatchIds,
  getTypographySwatchIds,
  getTypographyTypographyIds,
  ImageControlType,
  LinkControlType,
  ListControlType,
  RichTextControlType,
  ShapeControlType,
  SlotControlType,
  StyleControlType,
  StyleV2ControlType,
  TypographyControlType
} from "../controls";
import {
  getRichTextPageIds,
  getRichTextSwatchIds,
  getRichTextTypographyIds
} from "../controls/rich-text/introspection";
import {
  getRichTextV2PageIds,
  getRichTextV2SwatchIds,
  getRichTextV2TypographyIds
} from "../controls/rich-text-v2/introspection";
import {
  RichTextV2ControlType,
  isRichTextV1Data
} from "../controls/rich-text-v2/rich-text-v2";
import { match, P } from "ts-pattern";
function getElementChildren(descriptor, prop) {
  if (prop == null)
    return [];
  switch (descriptor.type) {
    case Types.Grid:
      return prop.elements;
    case SlotControlType:
      return prop.elements;
    case ListControlType:
      return prop.flatMap(
        ({ value }) => getElementChildren(descriptor.config.type, value)
      );
    case ShapeControlType: {
      return getShapeElementChildren(descriptor, prop);
    }
    case ListControlType: {
      return getListElementChildren(descriptor, prop);
    }
    case Types.Shape: {
      return getShapePropControllerElementChildren(descriptor, prop);
    }
    case Types.List: {
      return getListPropControllerElementChildren(descriptor, prop);
    }
    default:
      return [];
  }
}
function getElementId(descriptor, prop) {
  if (prop == null)
    return null;
  switch (descriptor.type) {
    case Types.ElementID:
      return prop;
    default:
      return null;
  }
}
function getBackgroundsSwatchIds(value) {
  return value?.flatMap((override) => override.value).flatMap((backgroundItem) => {
    switch (backgroundItem.type) {
      case "color":
        return backgroundItem.payload?.swatchId == null ? [] : [backgroundItem.payload.swatchId];
      case "gradient":
        return backgroundItem.payload.stops.flatMap(
          (stop) => stop.color == null ? [] : stop.color.swatchId
        );
      default:
        return [];
    }
  }) ?? [];
}
function getBorderSwatchIds(value) {
  return value?.flatMap((override) => override.value).flatMap((borderValue) => {
    return [
      borderValue.borderTop?.color?.swatchId,
      borderValue.borderRight?.color?.swatchId,
      borderValue.borderBottom?.color?.swatchId,
      borderValue.borderLeft?.color?.swatchId
    ].filter((swatchId) => swatchId != null);
  }) ?? [];
}
function getBoxShadowsSwatchIds(value) {
  return value?.flatMap((override) => override.value).map((item) => item.payload.color?.swatchId).filter((swatchId) => swatchId != null) ?? [];
}
function getResponsiveColorSwatchIds(value) {
  return value?.map((override) => override.value).map((color) => color.swatchId) ?? [];
}
function getSwatchIds(descriptor, prop) {
  if (prop == null)
    return [];
  switch (descriptor.type) {
    case Types.Backgrounds:
      return getBackgroundsSwatchIds(prop);
    case Types.Border:
      return getBorderSwatchIds(prop);
    case Types.NavigationLinks: {
      const value = prop;
      return value?.flatMap((item) => {
        switch (item.type) {
          case "button":
          case "dropdown":
            return [
              ...item.payload.color?.map((override) => override.value).map((color) => color.swatchId) ?? [],
              ...item.payload.textColor?.map((override) => override.value).map((color) => color.swatchId) ?? []
            ];
        }
      }) ?? [];
    }
    case Types.ResponsiveColor:
      return getResponsiveColorSwatchIds(prop);
    case Types.Shadows:
      return getBoxShadowsSwatchIds(prop);
    case RichTextControlType:
    case Types.RichText: {
      return getRichTextSwatchIds(prop);
    }
    case ColorControlType: {
      const value = prop;
      return value?.swatchId == null ? [] : [value.swatchId];
    }
    case TypographyControlType: {
      return getTypographySwatchIds(prop);
    }
    case StyleControlType: {
      return getStyleSwatchIds(prop);
    }
    case StyleV2ControlType: {
      const value = prop;
      return value?.flatMap((value2) => getSwatchIds(descriptor.config.type, value2.value)) ?? [];
    }
    case RichTextV2ControlType: {
      const data = prop;
      if (isRichTextV1Data(data))
        return getRichTextSwatchIds(data);
      return getRichTextV2SwatchIds(descriptor, data);
    }
    case ShapeControlType: {
      return getShapeSwatchIds(descriptor, prop);
    }
    case ListControlType: {
      return getListSwatchIds(descriptor, prop);
    }
    case Types.Shape: {
      return getShapePropControllerSwatchIds(descriptor, prop);
    }
    case Types.List: {
      return getListPropControllerSwatchIds(descriptor, prop);
    }
    default:
      return [];
  }
}
function getBackgroundsFileIds(value) {
  return value?.flatMap((override) => override.value).flatMap((backgroundItem) => {
    return match(backgroundItem).with({ type: "image-v1", payload: { image: { type: "makeswift-file" } } }, (item) => [
      item.payload.image.id
    ]).with({ type: "image", payload: { imageId: P.string } }, (item) => [item.payload.imageId]).otherwise(() => []);
  }) ?? [];
}
function getFileIds(descriptor, prop) {
  if (prop == null)
    return [];
  switch (descriptor.type) {
    case Types.Backgrounds:
      return getBackgroundsFileIds(prop);
    case Types.Image: {
      return match(prop).with(P.string, (v) => [v]).with({ type: "makeswift-file", version: 1 }, (v) => [v.id]).with({ type: "external-file", version: 1 }, () => []).exhaustive();
    }
    case Types.Images: {
      const value = prop;
      return value?.flatMap(
        (item) => match(item.props.file).with(P.string, (f) => [f]).with({ type: "makeswift-file", version: 1 }, (f) => [f.id]).with({ type: "external-file", version: 1 }, () => []).with(P.nullish, () => []).otherwise(() => [])
      ) ?? [];
    }
    case ImageControlType: {
      const value = prop;
      return match(value).with(P.string, (f) => [f]).with({ type: "makeswift-file" }, (f) => [f.id]).with({ type: "external-file" }, () => []).otherwise(() => []);
    }
    case ShapeControlType: {
      return getShapeFileIds(descriptor, prop);
    }
    case ListControlType: {
      return getListFileIds(descriptor, prop);
    }
    case Types.Shape: {
      return getShapePropControllerFileIds(descriptor, prop);
    }
    case Types.List: {
      return getListPropControllerFileIds(descriptor, prop);
    }
    default:
      return [];
  }
}
function getTypographyIds(descriptor, prop) {
  if (prop == null)
    return [];
  switch (descriptor.type) {
    case TypographyControlType: {
      return getTypographyTypographyIds(prop);
    }
    case RichTextControlType:
    case Types.RichText: {
      return getRichTextTypographyIds(prop);
    }
    case RichTextV2ControlType: {
      const data = prop;
      if (isRichTextV1Data(data))
        return getRichTextTypographyIds(data);
      return getRichTextV2TypographyIds(descriptor, data);
    }
    case ShapeControlType: {
      return getShapeTypographyIds(descriptor, prop);
    }
    case ListControlType: {
      return getListTypographyIds(descriptor, prop);
    }
    case Types.Shape: {
      return getShapePropControllerTypographyIds(descriptor, prop);
    }
    case Types.List: {
      return getListPropControllerTypographyIds(descriptor, prop);
    }
    default:
      return [];
  }
}
function getTableIds(descriptor, prop) {
  if (prop == null)
    return [];
  switch (descriptor.type) {
    case Types.Table: {
      const value = prop;
      return value == null ? [] : [value];
    }
    default:
      return [];
  }
}
function getPageIds(descriptor, prop) {
  if (prop == null)
    return [];
  switch (descriptor.type) {
    case Types.Link: {
      const value = prop;
      if (value == null)
        return [];
      switch (value.type) {
        case "OPEN_PAGE":
          return value.payload.pageId == null ? [] : [value.payload.pageId];
        default:
          return [];
      }
    }
    case LinkControlType: {
      const value = prop;
      if (value == null)
        return [];
      switch (value.type) {
        case "OPEN_PAGE":
          return value.payload.pageId == null ? [] : [value.payload.pageId];
        default:
          return [];
      }
    }
    case Types.NavigationLinks: {
      const value = prop;
      if (value == null)
        return [];
      return value?.flatMap((item) => {
        switch (item.type) {
          case "button": {
            if (item.payload.link == null)
              return [];
            switch (item.payload.link.type) {
              case "OPEN_PAGE":
                return item.payload.link.payload.pageId == null ? [] : [item.payload.link.payload.pageId];
              default:
                return [];
            }
          }
          case "dropdown": {
            return item.payload.links?.flatMap((link) => {
              if (link.payload.link == null)
                return [];
              switch (link.payload.link.type) {
                case "OPEN_PAGE":
                  return link.payload.link.payload.pageId == null ? [] : [link.payload.link.payload.pageId];
                default:
                  return [];
              }
            }) ?? [];
          }
        }
      }) ?? [];
    }
    case RichTextControlType:
    case Types.RichText: {
      return getRichTextPageIds(prop);
    }
    case RichTextV2ControlType: {
      const data = prop;
      if (isRichTextV1Data(data))
        return getRichTextPageIds(data);
      return getRichTextV2PageIds(descriptor, data);
    }
    case ShapeControlType: {
      return getShapePageIds(descriptor, prop);
    }
    case ListControlType: {
      return getListPageIds(descriptor, prop);
    }
    case Types.Shape: {
      return getShapePropControllerPageIds(descriptor, prop);
    }
    case Types.List: {
      return getListPropControllerPageIds(descriptor, prop);
    }
    default:
      return [];
  }
}
export {
  getBackgroundsFileIds,
  getBackgroundsSwatchIds,
  getBorderSwatchIds,
  getBoxShadowsSwatchIds,
  getElementChildren,
  getElementId,
  getFileIds,
  getPageIds,
  getResponsiveColorSwatchIds,
  getSwatchIds,
  getTableIds,
  getTypographyIds
};
//# sourceMappingURL=introspection.js.map